import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/max';
import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_COUNTRY, splitE164, toE164 } from 'src/utils/phone';
const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl ? new Intl.DisplayNames(['en'], {
  type: 'region'
}) : undefined;
const countryName = code => {
  var _regionNames$of;
  return (_regionNames$of = regionNames === null || regionNames === void 0 ? void 0 : regionNames.of(code)) !== null && _regionNames$of !== void 0 ? _regionNames$of : code;
};
const countries = getCountries().map(code => ({
  code,
  name: countryName(code),
  calling: getCountryCallingCode(code)
})).sort((a, b) => a.code === DEFAULT_COUNTRY ? -1 : b.code === DEFAULT_COUNTRY ? 1 : a.name.localeCompare(b.name));
export const PhoneNumberField = props => {
  const id = props.id,
    name = props.name,
    label = props.label,
    value = props.value,
    onChange = props.onChange,
    onBlur = props.onBlur,
    disabled = props.disabled,
    error = props.error,
    helperText = props.helperText,
    _props$countryLabel = props.countryLabel,
    countryLabel = _props$countryLabel === void 0 ? 'Country' : _props$countryLabel;
  const _useState = useState(() => {
      const initial = splitE164(value);
      return {
        country: initial.country || DEFAULT_COUNTRY,
        national: initial.national
      };
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];

  // Mirrors `state` synchronously so handlers can compute the emitted E.164 from the value that
  // is about to be set, rather than from a possibly-stale render closure.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Formik `enableReinitialize` (or a form reset) can swap `value` after mount. Re-derive local
  // state from it, but only when it disagrees with what our own local state would produce —
  // otherwise an incomplete in-progress number (which emits '') would get clobbered back to ''.
  useEffect(() => {
    if (toE164(stateRef.current.country, stateRef.current.national) !== value) {
      const next = splitE164(value);
      setState({
        country: next.country || DEFAULT_COUNTRY,
        national: next.national
      });
    }
  }, [value]);
  const handleCountryChange = e => {
    const country = e.target.value;
    // An in-progress '+'-prefixed number carries its own country code and would silently override
    // the selection the user just made, so an explicit country choice discards it.
    const national = stateRef.current.national.startsWith('+') ? '' : stateRef.current.national;
    const next = {
      country,
      national
    };
    setState(next);
    onChange(toE164(next.country, next.national));
  };

  // Only the number input reports blur, and only once something has been typed: blurring the
  // country selector or an untouched empty field should not mark the form field as touched.
  const handleNationalBlur = e => {
    if (onBlur && /[0-9+]/.test(stateRef.current.national)) onBlur(e);
  };
  const handleNationalChange = e => {
    const raw = e.target.value;

    // A leading '+' means the user is typing (or pasted) a full international number, which may
    // still be incomplete (e.g. '+44'). Keep the '+' visible in the field while it's incomplete —
    // stripping it back to bare digits on every keystroke made international entry unusable and
    // never let an in-progress '+' number validate once it was complete.
    if (raw.startsWith('+')) {
      const candidate = "+".concat(raw.slice(1).replace(/[^0-9]/g, ''));
      const asEntered = toE164(stateRef.current.country, candidate);
      if (asEntered) {
        const next = splitE164(asEntered);
        setState({
          country: next.country || DEFAULT_COUNTRY,
          national: next.national
        });
        onChange(asEntered);
      } else {
        setState(s => _objectSpread(_objectSpread({}, s), {}, {
          national: candidate
        }));
        onChange('');
      }
      return;
    }
    const national = raw.replace(/[^0-9]/g, '');
    const next = _objectSpread(_objectSpread({}, stateRef.current), {}, {
      national
    });
    setState(next);
    onChange(toE164(next.country, next.national));
  };
  return /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 8,
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(FormControl, {
    sx: {
      minWidth: 110
    },
    disabled: disabled,
    error: error
  }, /*#__PURE__*/React.createElement(InputLabel, {
    id: "".concat(id, "-country-label")
  }, countryLabel), /*#__PURE__*/React.createElement(Select, {
    labelId: "".concat(id, "-country-label"),
    id: "".concat(id, "-country"),
    label: countryLabel,
    value: state.country,
    onChange: handleCountryChange,
    renderValue: code => "".concat(code, " +").concat(getCountryCallingCode(code)),
    MenuProps: {
      PaperProps: {
        sx: {
          maxHeight: 320
        }
      }
    }
  }, countries.map(c => /*#__PURE__*/React.createElement(MenuItem, {
    key: c.code,
    value: c.code
  }, c.name, " (+", c.calling, ")")))), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    id: id,
    name: name,
    type: "tel",
    autoComplete: "tel-national",
    variant: "outlined",
    label: label,
    value: state.national,
    onChange: handleNationalChange,
    onBlur: handleNationalBlur,
    disabled: disabled,
    error: error,
    helperText: helperText
  }));
};